<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbSectionedList } from "@unio/saltbox-ui"
import { ref } from "vue"

import ContainerRow from "@/components/ContainerRow.vue"
import ContainerBreadcrumb from "@/components/containers/ContainerBreadcrumb.vue"
import ContainerFormSlideover from "@/components/containers/ContainerFormSlideover.vue"
import ContainerLooseItems from "@/components/containers/ContainerLooseItems.vue"
import ContainerScopeCard from "@/components/containers/ContainerScopeCard.vue"
import SearchInput from "@/components/SearchInput.vue"
import { useNavbar } from "@/composables/useNavbar"
import { useSearchableIndex } from "@/composables/useSearchableIndex"
import { ICON } from "@/lib/icons"
import { containersPath } from "@/routes"
import type {
  BreadcrumbSegment,
  ContainerFormData,
  ContainerSummary,
  ItemSummary,
  ScopeContainer,
  SelectOption,
} from "@/types/models"

const { title } = useNavbar({ title: "Containers" })

interface Props {
  mode: "root" | "search" | "drilled"
  containers: ContainerSummary[]
  query: string
  archive_scope: "active" | "archived"
  loose_items?: ItemSummary[]
  scope_container?: ScopeContainer
  breadcrumb?: BreadcrumbSegment[]
  parent_containers: SelectOption[]
  item_types: SelectOption[]
}

const props = defineProps<Props>()

const { search } = useSearchableIndex(props.query, containersPath, (): Record<string, string> =>
  props.archive_scope === "archived" ? { scope: "archived" } : {},
)

const isDrilled = props.mode === "drilled"
const containerVariant = isDrilled ? "drilled" : "root"

const slideoverOpen = ref(false)
const editContainer = ref<ContainerFormData | null>(null)

function openCreate() {
  editContainer.value = null
  slideoverOpen.value = true
}

function selectArchiveScope(scope: "active" | "archived") {
  router.visit(containersPath(), {
    data: scope === "archived" ? { scope } : {},
    preserveState: true,
  })
}

function openEdit() {
  if (!props.scope_container) return
  const sc = props.scope_container
  editContainer.value = {
    id: sc.id,
    name: sc.name,
    parent_id: sc.parent_id,
    item_type_id: sc.item_type_id,
    asset_code: sc.asset_code,
    assigned_location: sc.assigned_location,
    access_method: sc.access_method,
    access_notes: sc.access_notes,
  }
  slideoverOpen.value = true
}

const defaultParentId = isDrilled ? props.scope_container?.id : undefined
</script>

<template>
  <Head :title="title" />

  <div class="bg-elevated flex w-fit rounded-md p-1" aria-label="Inventory status">
    <button
      v-for="scope in ['active', 'archived'] as const"
      :key="scope"
      :data-filter="scope"
      :aria-pressed="archive_scope === scope"
      class="rounded px-4 py-1.5 text-sm capitalize"
      :class="
        archive_scope === scope ? 'bg-default text-highlighted' : 'text-muted hover:text-default'
      "
      @click="selectArchiveScope(scope)"
    >
      {{ scope }}
    </button>
  </div>

  <!-- Root / Search mode -->
  <template v-if="!isDrilled">
    <div class="flex items-center gap-4">
      <SearchInput v-model="search" placeholder="Search containers..." class="flex-1" />
      <UButton
        v-if="archive_scope === 'active'"
        :icon="ICON.add"
        label="New"
        color="neutral"
        variant="solid"
        class="hidden md:inline-flex"
        @click="openCreate"
      />
    </div>

    <div v-if="containers.length" class="flex flex-col gap-2">
      <span class="text-muted text-xs">{{ containers.length }} containers</span>
      <SbList bordered separator>
        <ContainerRow
          v-for="container in containers"
          :key="container.id"
          :container="container"
          :variant="containerVariant"
        />
      </SbList>
    </div>

    <UEmpty
      v-else
      :icon="ICON.container"
      :title="
        archive_scope === 'archived'
          ? 'No archived containers'
          : query
            ? 'No matching containers'
            : 'No containers yet'
      "
      :description="
        archive_scope === 'archived'
          ? 'Archived containers will appear here.'
          : query
            ? 'Try a different search term.'
            : 'Create your first container to start organizing.'
      "
      :actions="
        query || archive_scope === 'archived' ? [] : [{ label: 'New Container', click: openCreate }]
      "
    />
  </template>

  <!-- Drilled mode -->
  <template v-else>
    <div class="flex items-center justify-between gap-4">
      <ContainerBreadcrumb :segments="breadcrumb!" />
      <UButton
        v-if="archive_scope === 'active'"
        :icon="ICON.add"
        label="New"
        color="neutral"
        variant="solid"
        class="hidden shrink-0 md:inline-flex"
        @click="openCreate"
      />
    </div>
    <ContainerScopeCard
      :container="scope_container!"
      @edit="archive_scope === 'active' && openEdit()"
    />

    <SbSectionedList v-if="containers.length" label="Sub-containers" separator>
      <ContainerRow
        v-for="container in containers"
        :key="container.id"
        :container="container"
        variant="drilled"
      />
    </SbSectionedList>

    <ContainerLooseItems
      v-if="loose_items?.length"
      :label="`Items in ${scope_container!.name}`"
      :items="loose_items!"
    />

    <UEmpty
      v-if="!containers.length && !loose_items?.length"
      :icon="ICON.container"
      title="Empty container"
      description="This container has no sub-containers or items yet."
    />
  </template>

  <ContainerFormSlideover
    v-if="archive_scope === 'active'"
    v-model:open="slideoverOpen"
    :container="editContainer"
    :parent-containers="parent_containers"
    :item-types="item_types"
    :default-parent-id="defaultParentId"
  />
</template>
